import { QuestionCircleOutlined } from '@ant-design/icons'
import { Checkbox, Radio, Space, Tooltip } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'

import { LabelFilterMode } from '../../utils/useLabelFilter'
import { LabelSelectInput } from '../LabelSelectInput/LabelSelectInput'
import { TableFilterDropdownContainer } from '../TableFilterDropdownContainer/TableFilterDropdownContainer'

export interface TableLabelFilterDropdownProps {
  packed: string
  setSelectedKeys: (keys: React.Key[]) => void
  confirm: () => void
  clearFilters?: () => void
  close: () => void
}

const tooltipTitle = (
  <div className="flex flex-col space-y-1">
    <div>
      <strong>Všechny:</strong> Záznam musí obsahovat všechny vybrané štítky
    </div>
    <div>
      <strong>Alespoň jeden:</strong> Záznam musí obsahovat alespoň jeden
      vybraný štítek
    </div>
    <div>
      <strong>Neobsahuje:</strong> Vyloučí záznamy obsahující vybrané štítky
    </div>
  </div>
)

export const TableLabelFilterDropdown: React.FC<TableLabelFilterDropdownProps> =
  props => {
    const parts = useMemo(
      () => props.packed.split('|').filter(Boolean),
      [props.packed]
    )
    const initialMode =
      parts[0] === LabelFilterMode.ANY || parts[0] === LabelFilterMode.ALL
        ? (parts[0] as LabelFilterMode)
        : LabelFilterMode.ALL

    const excludeIndex = parts.indexOf('EXCLUDE')

    const initialExclude =
      excludeIndex === -1 ? [] : parts.slice(excludeIndex + 1)
    const initialInclude =
      excludeIndex === -1
        ? parts.slice(initialMode === parts[0] ? 1 : 0)
        : parts.slice(initialMode === parts[0] ? 1 : 0, excludeIndex)

    const [mode, setMode] = useState<LabelFilterMode>(initialMode)
    const [includeIds, setIncludeIds] = useState<string[]>(initialInclude)
    const [excludeEnabled, setExcludeEnabled] = useState<boolean>(
      initialExclude.length > 0
    )
    const [excludeIds, setExcludeIds] = useState<string[]>(initialExclude)

    const buildPacked = useCallback(
      (m: LabelFilterMode, inc: string[], exEnabled: boolean, ex: string[]) => {
        if (!inc.length && (!exEnabled || !ex.length)) return []
        const base: string[] = [m]
        if (inc.length) base.push(...inc.slice().sort())
        if (exEnabled && ex.length) base.push('EXCLUDE', ...ex.slice().sort())
        return [base.join('|')]
      },
      []
    )

    const emit = useCallback(
      (
        nextMode: LabelFilterMode,
        inc: string[],
        exEnabled: boolean,
        ex: string[]
      ) => {
        props.setSelectedKeys(buildPacked(nextMode, inc, exEnabled, ex))
      },
      [props, buildPacked]
    )

    const updateInclude = useCallback(
      (ids: string[]) => {
        setIncludeIds(ids)
        emit(mode, ids, excludeEnabled, excludeIds)
      },
      [emit, mode, excludeEnabled, excludeIds]
    )

    const updateExclude = useCallback(
      (ids: string[]) => {
        setExcludeIds(ids)
        emit(mode, includeIds, excludeEnabled, ids)
      },
      [emit, mode, excludeEnabled, includeIds]
    )

    const handleModeChange = useCallback(
      (next: LabelFilterMode) => {
        setMode(next)
        emit(next, includeIds, excludeEnabled, excludeIds)
      },
      [emit, includeIds, excludeEnabled, excludeIds]
    )

    const handleExcludeToggle = useCallback(
      (checked: boolean) => {
        setExcludeEnabled(checked)
        emit(mode, includeIds, checked, excludeIds)
      },
      [emit, mode, includeIds, excludeIds]
    )

    const handleClear = useCallback(() => {
      props.clearFilters?.()
      props.setSelectedKeys([])
      setIncludeIds([])
      setExcludeIds([])
      setExcludeEnabled(false)
      setMode(LabelFilterMode.ALL)
    }, [props])

    return (
      <TableFilterDropdownContainer
        className="w-[600px]"
        clearFilters={handleClear}
        confirm={props.confirm}
        close={props.close}
        isSelected={
          !!includeIds.length || (excludeEnabled && !!excludeIds.length)
        }
      >
        <Space direction="vertical" className="w-full">
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              <div>Obsahuje</div>
              <Radio.Group
                size="small"
                value={mode}
                onChange={e => handleModeChange(e.target.value)}
              >
                <Radio.Button value={LabelFilterMode.ALL}>Všechny</Radio.Button>
                <Radio.Button value={LabelFilterMode.ANY}>
                  Alespoň jeden
                </Radio.Button>
              </Radio.Group>
            </div>
            <Tooltip title={tooltipTitle}>
              <span className="text-gray-500 select-none p-1 cursor-help">
                <QuestionCircleOutlined className="mt-1 text-gray-400" />
              </span>
            </Tooltip>
          </div>

          <LabelSelectInput
            value={includeIds}
            onChange={v => updateInclude(v as string[])}
            disableNew
          />

          <div className="pt-1">
            <Checkbox
              checked={excludeEnabled}
              onChange={e => handleExcludeToggle(e.target.checked)}
            >
              Neobsahuje
            </Checkbox>

            {excludeEnabled && (
              <div className="my-2">
                <LabelSelectInput
                  value={excludeIds}
                  onChange={v => updateExclude(v as string[])}
                  disableNew
                />
              </div>
            )}
          </div>
        </Space>
      </TableFilterDropdownContainer>
    )
  }
